import {
    ArticleUl,
    Big,
    BoxText,
    CenteredUl,
    CheckList,
    CheckListUl,
    CopytextCard,
    CopytextExtraLarge,
    CopytextExtraSmall,
    CopytextFaqIcon,
    CopytextFaqIconSmall,
    CopytextImageTextSlider,
    CopytextLarge,
    CopytextSmall,
    CopytextTeaser,
    HeadingBase,
    HeadingExtraSmall,
    HeadingLarge,
    HeadingSeo,
    HeadingSmall,
    HeadingTeaser,
    Italic,
    Li,
    Quote,
    QuoteTextImage,
    QuoteTextImageBottom,
    Small,
    SmallCheckList,
    SmallUl,
    Strong,
    SuperSized,
    Ul,
} from "./components/typography"
import {
    BigIcon,
    CheckListIcon,
    H1Icon,
    H2Icon,
    H3Icon,
    H4Icon,
    QuoteIcon,
    SeoIcon,
    SmallIcon,
    SupIcon,
    SuperSizedIcon,
    UlIcon,
} from "./components/svg"
import { Link, RichTextExt, blockPluginConstructor, markPluginConstructor, plugins, types } from "react-bricks/frontend"
import { RenderElementProps, RenderLeafProps } from "slate-react"

import CopytextArticle from "./components/typography/copytext-article"
import CopytextImageTextColumn from "./components/typography/copytext-image-text-column"
import CopytextQuote from "./components/typography/copytext-quote"
import React from "react"

export type TypographyBlock =
    | "box-text"
    | "copytext-large"
    | "copytext-quote"
    | "copytext-extra-large"
    | "copytext-small"
    | "copytext-card"
    | "copytext-extra-small"
    | "copytext-teaser"
    | "copytext-article"
    | "copytext-faq-icon"
    | "copytext-faq-icon-small"
    | "quote-text-image"
    | "quote-text-image-bottom"
    | "image-text-slider"
    | "image-text-column"
    | "heading-teaser"
export type TypographyAllowItem =
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "ul"
    | "link"
    | "bold"
    | "sup"
    | "superSized"
    | "small"
    | "smallCheckList"
    | "articleUl"
    | "italic"
    | "checkList"
    | "centeredCheckList"
    | "big"
    | "quote"
    | "seo-h1"

export interface TypographyProps {
    block?: TypographyBlock
    allowList?: TypographyAllowItem[]
    propName?: string
    id?: string
    hidden?: boolean
    linkArrow?: boolean
}

type TypographyText = {
    text?: string
    bold?: true
    sup?: true
    italic?: true
    big?: true
    superSized?: true
    type?: "li" | "CheckList Item"
    children?: TypographyText[]
}

export type TypographyElement = {
    type: "paragraph" | TypographyAllowItem
    children: TypographyText[]
}

const Typography = ({
    block = "copytext-large",
    allowList = [
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "ul",
        "link",
        "bold",
        "sup",
        "superSized",
        "small",
        "smallCheckList",
        "articleUl",
        "italic",
        "checkList",
        "centeredCheckList",
        "big",
        "quote",
        "seo-h1",
    ],
    propName = "text",
    id,
    hidden = false,
    linkArrow,
}: TypographyProps) => {
    const blocks = {
        "box-text": BoxText,
        "copytext-large": CopytextLarge,
        "copytext-quote": CopytextQuote,
        "copytext-extra-large": CopytextExtraLarge,
        "copytext-small": CopytextSmall,
        "copytext-card": CopytextCard,
        "copytext-extra-small": CopytextExtraSmall,
        "copytext-teaser": CopytextTeaser,
        "copytext-article": CopytextArticle,
        "copytext-faq-icon": CopytextFaqIcon,
        "copytext-faq-icon-small": CopytextFaqIconSmall,
        "quote-text-image": QuoteTextImage,
        "image-text-slider": CopytextImageTextSlider,
        "quote-text-image-bottom": QuoteTextImageBottom,
        "image-text-column": CopytextImageTextColumn,
        "heading-teaser": HeadingTeaser,
    }

    const {
        heading1: h1,
        heading2: h2,
        heading3: h3,
        heading4: h4,
        heading5: h5,
        unorderedList,
        link,
        bold,
        italic,
    } = plugins

    const sup = markPluginConstructor({
        name: "sup",
        label: "Superscript",
        render: ({ children, attributes }: RenderLeafProps) => <sup {...attributes}>{children}</sup>,
        icon: SupIcon,
    })

    const small = blockPluginConstructor({
        name: "small",
        label: "Small",
        render: Small,
        icon: SmallIcon,
    })

    const centeredCheckList = blockPluginConstructor({
        name: "centeredCheckList",
        label: "CheckList",
        itemName: "CheckList Item",
        render: CenteredUl,
        renderItem: CheckList,
        icon: CheckListIcon,
    })

    const checkList = blockPluginConstructor({
        name: "checkList",
        label: "CheckList",
        itemName: "CheckList Item",
        render: CheckListUl,
        renderItem: CheckList,
        icon: CheckListIcon,
    })

    const big = markPluginConstructor({
        name: "big",
        label: "Big",
        render: Big,
        icon: BigIcon,
    })

    const quote = markPluginConstructor({
        name: "quote",
        label: "Quote",
        render: Quote,
        icon: QuoteIcon,
    })

    const smallCheckList = blockPluginConstructor({
        name: "smallCheckList",
        label: "Small CheckList",
        itemName: "CheckList Item",
        render: SmallUl,
        renderItem: SmallCheckList,
        icon: CheckListIcon,
    })

    const articleUl = blockPluginConstructor({
        name: "articleUl",
        label: "Article Ul",
        itemName: "List Item",
        render: ArticleUl,
        renderItem: Li,
        icon: UlIcon,
    })

    const superSized = markPluginConstructor({
        name: "superSized",
        label: "Super Sized",
        render: SuperSized,
        icon: SuperSizedIcon,
    })

    // for legacy reasons, to not break the existing projects font-sizes this needs to be a block-plugin
    const headingSize1 = blockPluginConstructor({
        name: "h1",
        label: "Size 1",
        render: HeadingLarge,
        icon: h1.button?.icon,
    })

    // because headingSize must be a block-plugin for legacy reasons
    // and only one block-plugin can be used for a selected text, this needs to be a mark-plugin
    const heading1 = markPluginConstructor({
        name: "heading1",
        label: "h1",
        hotKey: "mod+shift+1",
        render: ({ children }) => <h1 className="inline">{children}</h1>,
        icon: H1Icon,
    })

    // headingSize1 and headingSize2 are the same, but with different icons
    // this is for legacy reasons, to not break the existing projects font-sizes
    const headingSize2 = blockPluginConstructor({
        name: "h2",
        label: "Size 2",
        render: HeadingLarge,
        icon: h2.button?.icon,
    })

    const heading2 = markPluginConstructor({
        name: "heading2",
        label: "h2",
        hotKey: "mod+shift+2",
        render: ({ children }) => <h2 className="inline">{children}</h2>,
        icon: H2Icon,
    })

    const headingSize3 = blockPluginConstructor({
        name: "h3",
        label: "Size 3",
        render: HeadingBase,
        icon: h3.button?.icon,
    })

    const heading3 = markPluginConstructor({
        name: "heading3",
        label: "h3",
        hotKey: "mod+shift+3",
        render: ({ children }) => <h3 className="inline">{children}</h3>,
        icon: H3Icon,
    })

    const headingSize4 = blockPluginConstructor({
        name: "h4",
        label: "Size 4",
        render: HeadingSmall,
        icon: h4.button?.icon,
    })

    const heading4 = markPluginConstructor({
        name: "heading4",
        label: "h4",
        hotKey: "mod+shift+4",
        render: ({ children }) => <h4 className="inline">{children}</h4>,
        icon: H4Icon,
    })

    const headingSize5 = blockPluginConstructor({
        name: "h5",
        label: "Size 5",
        render: HeadingExtraSmall,
        icon: h5.button?.icon,
    })

    const headingSizeSeo = blockPluginConstructor({
        name: "seo-h1",
        label: "Size SEO",
        render: HeadingSeo,
        icon: SeoIcon,
    })

    const allowedPlugins: types.RichTextPlugin[] = [
        headingSize1,
        headingSize2,
        headingSize3,
        headingSize4,
        headingSize5,
        {
            ...unorderedList,
            renderElement: Ul,
            renderItemElement: Li,
        },
        articleUl,
        {
            ...link,
            renderElement: ({ children, attributes, element }: RenderElementProps) => (
                <Link
                    {...attributes}
                    href={element.data?.url || element.url}
                    target={element.data?.isTargetBlank ? "_blank" : undefined}
                    rel={element.data?.isTargetBlank ? "noopener" : undefined}
                    className="link [&:has(sup)]:whitespace-nowrap [&:not(:has(sup))]:underline hover:[&_sup]:underline"
                    tabIndex={hidden ? -1 : undefined}
                >
                    {children}
                </Link>
            ),
        },
        {
            ...bold,
            renderLeaf: Strong,
        },
        {
            ...italic,
            renderLeaf: Italic,
        },
        sup,
        headingSizeSeo,
        small,
        smallCheckList,
        centeredCheckList,
        checkList,
        big,
        quote,
        superSized,
    ].filter(p => allowList.includes(p.name as TypographyAllowItem))

    return (
        <div
            id={id}
            className={linkArrow ? "relative inline [&>div>div]:inline [&>div]:inline [&_p]:inline" : "relative"}
        >
            <RichTextExt
                propName={propName}
                placeholder=""
                renderBlock={blocks[block]}
                plugins={[heading1, heading2, heading3, heading4, ...allowedPlugins]}
            />
        </div>
    )
}

export default Typography
